import { Icon, type IconName } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { ChoiceCardGroup, type ChoiceCardOption } from "./ChoiceCardGroup.js";

export type DriveMode = "personas" | "competitor" | "custom";

/** Glyphs this feature already uses: audiences, a company mark, and a pencil for a hand-written set. */
const DRIVE_MODE_ICONS: Record<DriveMode, IconName> = {
  personas: "users",
  competitor: "building-2",
  custom: "pencil",
};

export interface DriveModeCardsProps {
  value: DriveMode;
  onChange: (mode: DriveMode) => void;
  disabled?: boolean;
  /** Leftover opportunity-set create. Hidden when the caller cannot generate a competitor set. */
  showCompetitor?: boolean;
  /** Same create permission as the dialog. Hidden when the caller cannot create a set. */
  showCustom?: boolean;
}

export function DriveModeCards({
  value,
  onChange,
  disabled,
  showCompetitor = true,
  showCustom = false,
}: DriveModeCardsProps) {
  const { t } = useTranslation();

  const options: ChoiceCardOption<DriveMode>[] = [
    {
      value: "personas",
      title: t("personaOpportunities.createDialog.driveMode.personas", "Personas driven"),
      icon: <Icon name={DRIVE_MODE_ICONS.personas} size="sm" color="inherit" aria-hidden />,
      description: t(
        "personaOpportunities.createDialog.driveMode.personasDescription",
        "Angles built from the audiences you want to reach.",
      ),
    },
    ...(showCompetitor
      ? [
          {
            value: "competitor" as const,
            title: t("personaOpportunities.createDialog.driveMode.competitor", "Competitor driven"),
            icon: <Icon name={DRIVE_MODE_ICONS.competitor} size="sm" color="inherit" aria-hidden />,
            description: t(
              "personaOpportunities.createDialog.driveMode.competitorDescription",
              "Angles built from what rival brands are running.",
            ),
          },
        ]
      : []),
    ...(showCustom
      ? [
          {
            value: "custom" as const,
            title: t("personaOpportunities.createDialog.driveMode.custom", "Custom"),
            icon: <Icon name={DRIVE_MODE_ICONS.custom} size="sm" color="inherit" aria-hidden />,
            description: t(
              "personaOpportunities.createDialog.driveMode.customDescription",
              "An empty set for imported or hand-written scripts. Nothing is generated.",
            ),
          },
        ]
      : []),
  ];

  return (
    <ChoiceCardGroup
      id="persona-set-drive-mode"
      label={t("personaOpportunities.createDialog.driveMode.label", "Opportunity type")}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      stackOnNarrow
    />
  );
}
